const data = require('./siteData.json');
const guides = require('./guides.json');
const {profiles, virtualProfiles, priorityProducts, pathForPriority} = require('./priority');

const slugify = value => String(value || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().trim().replace(/&/g, 'et').replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
const categorySlug = name => Object.keys(data.categories).find(key => data.categories[key].name === name);
const productPath = product => `/equipements/${categorySlug(product.category)}/${slugify(product.subcategory)}`;
const productSlug = product => slugify(product.name);
const productUrl = product => `/location/${productSlug(product)}`;

function trim(text, limit = 158) {
  const clean = String(text || '').replace(/\s+/g, ' ').trim();
  if (clean.length <= limit) return clean;
  return `${clean.slice(0, clean.lastIndexOf(' ', limit) || limit).replace(/[,;:.]$/, '')}…`;
}

function productPage(product) {
  const fallback = `${product.name} à louer chez 3DLoc, près du Havre et en Normandie. Retrait à 15 min d'Étretat et Honfleur, livraison sur devis.`;
  return page(`Location ${product.name} au Havre`, trim(product.description || fallback), {h1: `Location ${product.name}`, type: 'product', image: product.image || null});
}

function page(title, description, extra = {}) {
  return {title: `${title} | 3DLoc`, h1: title, description, status: 200, robots: 'index, follow', ...extra};
}

function familyPage(category, family, products) {
  const profile = profiles[family];
  if (profile?.category === category) return page(profile.title, profile.description, {h1:`Location ${profile.label.toLowerCase()} au Havre`});
  const entry = data.families[family];
  const product = products?.find(p => categorySlug(p.category) === category && slugify(p.subcategory) === family);
  if (!product && (!entry || entry[0] !== category)) return null;
  const label = entry?.[1] || product.subcategory.replace(/^location\s+/i, '').trim();
  return page(`Location ${label.toLowerCase()} au Havre`, `${label} à louer près du Havre et en Normandie : consultez les modèles et tarifs 3DLoc. Retrait facile et rapide, à 15 min d'Étretat et Honfleur, ou livraison sur devis.`, {h1: `Location ${label.toLowerCase()} au Havre`});
}

function resolveSeo(pathname, search = '', products = null) {
  let decoded;
  try { decoded = decodeURIComponent(pathname); } catch { decoded = '/404'; }
  const path = decoded.replace(/\/+$/, '') || '/';
  const parts = path.split('/').filter(Boolean);
  const params = new URLSearchParams(search);
  const last = slugify(parts[parts.length - 1]);
  let target;
  if (parts.length === 1 && data.aliases[last]) target = `/univers/${data.aliases[last]}`;
  if (parts.length >= 2 && (parts[0] === 'univers' || parts[0] === 'equipements')) {
    const category = data.aliases[slugify(parts[1])] || slugify(parts[1]);
    target = `/${parts[0]}/${category}${parts[2] ? `/${slugify(parts[2])}` : ''}`;
    if (parts.length > 3 || (parts[0] === 'univers' && parts.length > 2)) target = undefined;
  }
  if (path === '/categories' && !params.has('search')) {
    const category = categorySlug(params.get('category'));
    const family = slugify(params.get('subcategory'));
    const familyCategory = data.families[family]?.[0] || categorySlug(products?.find(p => slugify(p.subcategory) === family)?.category);
    if (family && familyCategory) target = `/equipements/${category || familyCategory}/${family}`;
    else if (category) target = `/equipements/${category}`;
  }
  const canonicalPath = target || path;
  const segments = canonicalPath.split('/').filter(Boolean);
  let result;
  if (data.staticPages[canonicalPath]) result = page(...data.staticPages[canonicalPath]);
  else if (['univers', 'equipements'].includes(segments[0]) && data.categories[segments[1]]) {
    const category = data.categories[segments[1]];
    if (segments.length === 2) result = page(segments[0] === 'univers' ? category.title : `${category.label} : matériel à louer au Havre`, segments[0] === 'univers' ? category.description : `Consultez le catalogue ${category.label.toLowerCase()} de 3DLoc : équipements et tarifs de location près du Havre et en Normandie. Retrait facile à 15 min d'Étretat et Honfleur, livraison sur devis.`, {h1: category.title});
    if (segments.length === 3 && segments[0] === 'equipements') result = familyPage(segments[1], segments[2], products);
  } else if (segments.length === 2 && segments[0] === 'location-materiel' && data.cities[segments[1]]) {
    const city = data.cities[segments[1]];
    const place = city === 'Le Havre' ? 'au Havre' : `à ${city}`;
    result = page(`Location de matériel ${place}`, `Travaux, jardinage, nettoyage et événementiel ${place} : consultez le matériel 3DLoc. Retrait et livraison en Normandie sur devis.`);
  } else if (segments.length === 2 && segments[0] === 'location-materiel-zone' && data.zones[segments[1]]) {
    const zone = data.zones[segments[1]];
    result = page(`Location de matériel : ${zone}`, `Découvrez les communes desservies par 3DLoc dans le secteur ${zone}. Matériel pour vos projets et conditions de livraison sur devis.`);
  } else if (segments.length === 2 && segments[0] === 'guides' && guides[segments[1]]) {
    const guide = guides[segments[1]];
    result = page(guide.title, guide.description, {h1: guide.h1, type: 'article', image: guide.image});
  } else if (segments.length === 2 && segments[0] === 'actualites' && data.articles[segments[1]]) {
    result = page(...data.articles[segments[1]].slice(0, 2), {type: 'article'});
  } else if (segments.length === 2 && segments[0] === 'reservation') {
    const product = products?.find(p => String(p.id) === segments[1]);
    if (product) return {...productPage(product), path: productUrl(product), redirect: productUrl(product)};
    if (products === null || /^\d+$/.test(segments[1])) result = page('Réservation de matériel', 'Retrouvez votre matériel et les possibilités de réservation chez 3DLoc.', {robots: 'noindex, follow'});
  } else if (segments.length === 2 && segments[0] === 'location') {
    const product = products?.find(p => productSlug(p) === segments[1]);
    if (product) result = productPage(product);
    else if (products === null) result = page('Location de matériel', 'Consultez la fiche de ce matériel de location 3DLoc : usage, tarifs et réservation près du Havre et en Normandie.');
  } else if (canonicalPath === '/admin' || canonicalPath === '/catalogue-pdf') {
    result = page(canonicalPath === '/admin' ? 'Administration' : 'Catalogue imprimable de matériel', '3DLoc – location de matériel en Normandie.', {robots: 'noindex, follow'});
  }
  if (!result) result = page('Page introuvable', 'Cette page n’existe pas. Retrouvez le catalogue de location 3DLoc.', {status: 404, robots: 'noindex, follow'});
  const filtered = params.has('search') || params.has('category') || params.has('subcategory');
  const redirect = result.status === 200 && canonicalPath !== pathname ? canonicalPath : null;
  if (params.get('search') && result.status === 200) result = page('Recherche de matériel à louer', 'Consultez les résultats de votre recherche dans le catalogue de location 3DLoc.', {robots:'noindex, follow'});
  return {...result, path: canonicalPath, redirect, robots: filtered && !redirect ? 'noindex, follow' : result.robots};
}

function sitemapPaths(products = null) {
  const paths = Object.keys(data.staticPages);
  Object.keys(data.categories).forEach(slug => paths.push(`/univers/${slug}`, `/equipements/${slug}`));
  Object.keys(data.cities).forEach(slug => paths.push(`/location-materiel/${slug}`));
  Object.keys(data.zones).forEach(slug => paths.push(`/location-materiel-zone/${slug}`));
  Object.keys(data.articles).forEach(slug => paths.push(`/actualites/${slug}`));
  Object.keys(guides).forEach(slug => paths.push(`/guides/${slug}`));
  if (products) products.forEach(p => {
    if (categorySlug(p.category) && p.subcategory) paths.push(productPath(p));
    if (productSlug(p)) paths.push(productUrl(p));
  });
  else Object.entries(data.families).forEach(([slug, [category]]) => paths.push(`/equipements/${category}/${slug}`));
  virtualProfiles().forEach(([key, profile]) => { if (!products || priorityProducts(products, profile, false).length) paths.push(pathForPriority(key)); });
  return [...new Set(paths)];
}

module.exports = {data, slugify, categorySlug, productPath, productSlug, productUrl, resolveSeo, sitemapPaths};